import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy, input, viewChild } from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { SessionService } from '../../services/session.service';
import { ServiceCompany, ServiceCompanyService } from '@eadsysteme/ewsrealestateclient-v2';
import { Contact, ContactService } from '@eadsysteme/contactclient-v1';
import {
	EedPrintService,
	EnergyEfficiencySettingService,
	PreviewDto,
	PropertyManagementSetting,
	ServiceCompanySetting,
} from '@eadsysteme/energyefficiencyclient-v1';
import { SnackbarService } from '../../services/snackbar.service';
import { LayoutService } from '../../../@vex/services/layout.service';
import { Icons } from '../../util/Icons';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { zip } from 'rxjs';
import { UviLetterComponent } from '../settings/uvi-letter/uvi-letter.component';
import { UviFooterComponent } from '../settings/uvi-footer/uvi-footer.component';
import { saveAs } from 'file-saver';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { ContactWithArea } from '../../util/ContactWithArea';
import { MatIcon } from '@angular/material/icon';
import { NgTooltipModule } from '@silmar/ng-tooltip';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/input';
import { AraSpinnerButtonComponent } from '../util/ara-spinner-button/ara-spinner-button.component';
import { MatButton } from '@angular/material/button';
import CompanyTypeEnum = ServiceCompany.CompanyTypeEnum;

@Component({
	selector: 'ara-uvi-letter-settings',
	templateUrl: './uvi-letter-settings.component.html',
	styleUrl: './uvi-letter-settings.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatIcon,
		NgTooltipModule,
		MatRadioGroup,
		ReactiveFormsModule,
		FormsModule,
		MatRadioButton,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		UviLetterComponent,
		AraSpinnerButtonComponent,
		MatButton,
	],
})
export class UviLetterSettingsComponent implements OnInit {
	private translocoService = inject(TranslocoService);
	private serviceCompanyService = inject(ServiceCompanyService);
	private contactService = inject(ContactService);
	private energyEfficiencySettingsService = inject(EnergyEfficiencySettingService);
	private eedPrintService = inject(EedPrintService);
	private snackBar = inject(SnackbarService);
	protected layoutService = inject(LayoutService);
	protected sessionService = inject(SessionService);
	protected icons = inject(Icons);
	protected breadCrumbService = inject(BreadCrumbService);

	private destroyRef = inject(DestroyRef);
	possiblePropertyManagements: Array<ContactWithArea> = [];
	selectedContact: ContactWithArea = null;
	currentArea: number;
	serviceCompanyEnabled = true;
	propertyManagementEnabled: boolean;
	propertyManagementName = '';
	public currentServiceCompanySetting: ServiceCompanySetting;
	public currentPropertyManagementSetting: PropertyManagementSetting;
	previewValid = false;
	coverLetter: string;
	allowEdit = signal(true);
	isMobile = signal(false);
	previewFinishedLoading = signal(true);
	formDirty = signal(false);

	public model = {
		coverText: '',
		footerTitle: '',
		footerText: '',
		selectedVariants: new Set(),
	};

	readonly coverLetterChild = viewChild(UviLetterComponent);
	readonly footerChild = viewChild(UviFooterComponent);
	private propertyManagementCover = '';
	private serviceCompanyCover = '';
	readonly presetContact = input<Contact>(undefined);

	ngOnInit(): void {
		this.currentArea = this.sessionService.effectiveArea;
		this.currentPropertyManagementSetting = this.getDefaultPropertyManagementSetting();
		this.currentServiceCompanySetting = this.getDefaultServiceCompanySetting();
		this.setupPropertyManagement();
	}

	changeContact(event: MatSelectChange) {
		const contactHelper = event.value;
		this.contactService
			.getContactById(contactHelper.area, contactHelper.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((contact) => {
				this.formChanged('changeContact');
				this.selectedContact = {
					area: contactHelper.area,
					id: contact.id,
					name: contact.name,
					accountNumber: contact.accountNumber,
				};
				this.selectedContact.area = contactHelper.area;
				this.propertyManagementName = ' (' + contact.name + ')';
				this.loadServiceCompanyAndPropertyManagementSettings(false);
			});
	}

	private setupPropertyManagement() {
		this.propertyManagementEnabled = false;
		const presetContact = this.presetContact();
		if (presetContact) {
			this.propertyManagementName = ' (' + presetContact.name + ')';
			this.loadServiceCompanyAndPropertyManagementSettings();
		} else if (this.sessionService.isPropertyManagement()) {
			const propertyManagementIds = this.sessionService.getPropertyManagementIdsAndAreas();
			if (propertyManagementIds.length > 0) {
				this.propertyManagementEnabled = true;
				propertyManagementIds.forEach((contactHelper) => {
					this.contactService
						.getContactById(contactHelper.area, contactHelper.id)
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe((contact) => {
							if (!contact) {
								this.snackBar.warn(this.translocoService.translate('errors.settingsNoContactUseServiceCompanySettings'));
								this.loadServiceCompanySettings();
								return;
							}
							if (!this.selectedContact) {
								this.selectedContact = {
									area: contactHelper.area,
									id: contact.id,
									name: contact.name,
									accountNumber: contact.accountNumber,
								};
								this.propertyManagementName = ' (' + contact.name + ')';
								this.loadServiceCompanyAndPropertyManagementSettings();
							}
							this.serviceCompanyService.getServiceCompany(contactHelper.area).subscribe((serviceCompany) => {
								let serviceCompanyName = serviceCompany.company?.companyNames[0]?.name;
								if (serviceCompany.company?.companyNames[1]) {
									serviceCompanyName += ', ' + serviceCompany.company?.companyNames[1]?.name;
								}
								this.possiblePropertyManagements.push({
									area: contactHelper.area,
									id: contact.id,
									name: contact.name,
									accountNumber: contact.accountNumber,
									serviceCompanyName: serviceCompanyName,
								});
							});
						});
				});
			} else {
				this.loadServiceCompanySettings();
			}
		} else {
			this.loadServiceCompanySettings();
		}
	}

	private async loadServiceCompanySettings() {
		this.energyEfficiencySettingsService
			.getServiceCompanySetting(this.currentArea)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (serviceCompanySetting) => {
					this.currentServiceCompanySetting = serviceCompanySetting;
					if (this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany) {
						this.coverLetter = serviceCompanySetting.coverLetter;
						this.serviceCompanyCover = serviceCompanySetting.coverLetter;
						if (!serviceCompanySetting.coverLetter) {
							this.serviceCompanyEnabled = false;
							this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany = false;
						}
					}
				},
			});
	}

	private loadServiceCompanyAndPropertyManagementSettings(overwriteReadCoverLetterFromServiceCompanySetting = true) {
		zip(
			this.energyEfficiencySettingsService.getServiceCompanySetting(this.currentArea),
			this.energyEfficiencySettingsService.getPropertyManagementSetting(this.currentArea, this.selectedContact.id)
		).subscribe((result) => {
			const serviceCompanySetting = result[0];
			const propertyManagementSetting = result[1];
			this.setPropertyManagementSetting(propertyManagementSetting, this.selectedContact, overwriteReadCoverLetterFromServiceCompanySetting);
			this.setServiceCompanySetting(serviceCompanySetting);
			this.setCoverletter(propertyManagementSetting, serviceCompanySetting);
			if (!serviceCompanySetting.coverLetter) {
				this.serviceCompanyEnabled = false;
				this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany = false;
			}
			// trigger change detection
			this.currentPropertyManagementSetting = { ...this.currentPropertyManagementSetting };
			this.checkEdit();
		});
	}

	private setPropertyManagementSetting(
		propertyManagementSetting: PropertyManagementSetting,
		contact: Contact,
		overwriteReadCoverLetterFromServiceCompanySetting: boolean
	) {
		if (!propertyManagementSetting) {
			throw new Error('Could not find propertyManagementSetting for area ' + this.currentArea + ' and id ' + contact.id);
		} else {
			if (!overwriteReadCoverLetterFromServiceCompanySetting) {
				propertyManagementSetting.readCoverLetterFromServiceCompany = this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany;
			}
			this.currentPropertyManagementSetting = propertyManagementSetting;
			if (propertyManagementSetting.coverLetter) {
				this.propertyManagementCover = propertyManagementSetting.coverLetter;
			} else {
				this.currentPropertyManagementSetting.coverLetter = this.getDefaultPropertyManagementSetting().coverLetter;
				this.propertyManagementCover = this.getDefaultPropertyManagementSetting().coverLetter;
			}
		}
	}

	private setServiceCompanySetting(serviceCompanySetting: ServiceCompanySetting) {
		if (!serviceCompanySetting) {
			throw new Error('Could not find serviceCompanySetting for area ' + this.currentArea);
		} else {
			this.currentServiceCompanySetting = serviceCompanySetting;
			if (serviceCompanySetting.coverLetter) {
				this.serviceCompanyCover = serviceCompanySetting.coverLetter;
			} else {
				this.currentServiceCompanySetting.coverLetter = this.getDefaultServiceCompanySetting().coverLetter;
				this.serviceCompanyCover = this.getDefaultServiceCompanySetting().coverLetter;
			}
		}
	}

	private getDefaultServiceCompanySetting(): ServiceCompanySetting {
		return {
			area: this.currentArea,
			title: this.translocoService.translate('settings.defaultSCTitle'),
			footer: this.translocoService.translate('settings.defaultSCFooter'),
			coverLetter: this.translocoService.translate('settings.defaultSCCoverLetter'),
			selectedVariants: new Set(),
		};
	}

	private setCoverletter(propertyManagementSetting: PropertyManagementSetting, serviceCompanySetting: ServiceCompanySetting) {
		if (!this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany) {
			this.coverLetter = propertyManagementSetting.coverLetter;
		} else {
			this.coverLetter = serviceCompanySetting.coverLetter;
		}
	}

	public saveSettings() {
		this.currentServiceCompanySetting.area = this.currentArea;
		// @ts-ignore
		this.currentServiceCompanySetting.selectedVariants = [...this.currentServiceCompanySetting.selectedVariants];
		this.setCoverLetter();
		this.savePropertyManagementSetting();
	}

	private savePropertyManagementSetting() {
		this.currentPropertyManagementSetting.coverLetter = this.propertyManagementCover;
		this.energyEfficiencySettingsService.createOrUpdatePropertyManagementSetting(this.currentArea, this.currentPropertyManagementSetting).subscribe({
			next: (value) => {
				this.currentPropertyManagementSetting = value;
				this.snackBar.success(this.translocoService.translate('realestateProperties.propertiesSaved'));
			},
			error: (err) => {
				this.showErrorSnackbar(err);
			},
		});
	}

	async onShowPreview() {
		// await this.onSubmitPrintSettings();
		this.previewFinishedLoading.set(false);
		this.setCoverLetter();
		const previewDto: PreviewDto = {};
		previewDto.coverLetterText = this.coverLetter;
		previewDto.footerText = this.currentServiceCompanySetting.footer;
		previewDto.footerTitle = this.currentServiceCompanySetting.title;
		previewDto.companyType = this.companyTypeByString(this.sessionService.getCompanyNameFromUrl());
		previewDto.readCoverLetterFromServiceCompany = this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany;
		previewDto.propertyManagementId = this.currentPropertyManagementSetting.propertyManagementId;
		return this.eedPrintService.getReportPreview(this.currentArea, previewDto).subscribe({
			next: (value) => {
				if (value.size === 0) {
					this.snackBar.error(this.translocoService.translate('errors.settingsInvalidReport'));
				} else {
					const blob = new Blob([value], { type: 'application/pdf' });
					saveAs(blob, 'report.pdf');
				}
			},
			error: () => {
				this.snackBar.error(this.translocoService.translate('errors.settingsReportFailedTextTooLong'));
				this.previewValid = false;
			},
			complete: () => {
				this.previewFinishedLoading.set(true);
			},
		});
	}

	public updateCoverLetterSelection() {
		this.formChanged('coverLetterSelection');
		if (this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany) {
			this.propertyManagementCover = this.coverLetter;
			this.coverLetter = this.currentServiceCompanySetting.coverLetter;
		} else {
			this.serviceCompanyCover = this.coverLetter;
			this.coverLetter = this.currentPropertyManagementSetting.coverLetter;
		}
		this.checkEdit();
	}

	checkEdit() {
		this.allowEdit.set(
			!(
				(this.presetContact() && this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany) ||
				this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany
			)
		);
	}

	private getDefaultPropertyManagementSetting(): PropertyManagementSetting {
		return {
			propertyManagementId: '',
			coverLetter: this.translocoService.translate('settings.defaultCoverLetter'),
			readCoverLetterFromServiceCompany: true,
		};
	}

	private showErrorSnackbar(err) {
		if (err?.error?.message?.includes('Report has more than 1 page')) {
			this.snackBar.error(
				this.translocoService.translate('errors.propertiesNotSaved') + ' - ' + this.translocoService.translate('settings.reportFailedTextTooLong')
			);
		} else {
			this.snackBar.error(this.translocoService.translate('errors.propertiesNotSaved'));
		}
	}

	private setCoverLetter() {
		if (this.currentPropertyManagementSetting.readCoverLetterFromServiceCompany) {
			if (!this.coverLetter) {
				this.coverLetter = this.translocoService.translate('settings.defaultSCCoverLetter');
			}
			this.currentServiceCompanySetting.coverLetter = this.coverLetter;
			this.serviceCompanyCover = this.coverLetter;
		} else {
			if (!this.coverLetter) {
				this.coverLetter = this.translocoService.translate('settings.defaultCoverLetter');
			}
			this.currentPropertyManagementSetting.coverLetter = this.coverLetter;
			this.propertyManagementCover = this.coverLetter;
		}
	}

	private companyTypeByString(companyName: string): CompanyTypeEnum {
		switch (companyName) {
			case 'ead':
				return CompanyTypeEnum.EAD;
			case 'arasys':
				return CompanyTypeEnum.ARASYS;
			case 'deltat':
				return CompanyTypeEnum.DELTAT;
			default:
				return CompanyTypeEnum.ARASYS;
		}
	}

	protected formChanged(source: string) {
		if (source === 'coverLetter') {
			if (
				this.coverLetter &&
				this.coverLetter !== this.currentPropertyManagementSetting.coverLetter &&
				this.coverLetter !== this.currentServiceCompanySetting.coverLetter
			) {
				this.formDirty.set(true);
			}
		} else {
			this.formDirty.set(true);
		}
	}

	public compareContactsWithAreas(obj1: ContactWithArea, obj2: ContactWithArea) {
		return obj1.area === obj2.area && obj1.id === obj2.id;
	}
}
